import { useTheme } from "@mui/material/styles";
import React, { forwardRef, FunctionComponent, ReactNode, useState } from "react";
import Button, { ButtonProps } from "../button/Button";
import Menu, { MenuProps } from "./Menu";

export interface ButtonMenuProps extends Omit<ButtonProps, "onClick"> {
	/** indicates if user is hovering */
	hovering?: boolean;

	/** Label for button menu */
	label?: ReactNode;

	/** properties applied to Menu component */
	menuProps?: Partial<MenuProps>;

	/** handler for open menu */
	onOpen?: VoidFunction;

	/** handler for close menu */
	onClose?: VoidFunction;

	/** only show button when hovering */
	showOnHover?: boolean;

	/** set to true if menu should use the anchor element as its container (rather than be contained by document body) */
	useAnchorAsContainer?: boolean;
}

const ButtonMenu: FunctionComponent<ButtonMenuProps> = forwardRef((props, ref) => {
	const {
		children,
		hovering,
		label,
		menuProps,
		onOpen,
		onClose,
		showOnHover,
		useAnchorAsContainer,
		...otherProps
	} = props;

	const theme = useTheme();
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const open = !!anchorEl;

	const onClickButton = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
		event.currentTarget.setAttribute("aria-expanded", "true");
		onOpen?.();
	};

	const onCloseMenu = () => {
		anchorEl?.setAttribute("aria-expanded", "false");
		setAnchorEl(null);
		onClose?.();
	};

	if (open || hovering || !showOnHover) {
		return (
			<>
				<Button
					onClick={onClickButton}
					size="large"
					{...otherProps}
					ref={ref}
				>
					{label}
				</Button>
				<Menu
					align="right"
					anchorEl={anchorEl}
					container={useAnchorAsContainer ? anchorEl : null}
					onClose={onCloseMenu}
					open={open}
					sx={{
						marginTop: theme.spacing(1),
					}}
					{...menuProps}
				>
					{children}
				</Menu>
			</>
		);
	}
	return null;
});

export default ButtonMenu;
